import {
  ArrowPathIcon,
  ArrowUpRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import { ButtonGroup, FadeInBasic, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useTasteTestTx } from "../../../pages/TasteTestDetail/hooks/useTasteTestTx";
import {
  EOrderFlowState,
  EOrderTransactionBuilderState,
  useOrderContext,
} from "../../../stores/OrderContext";
import LoadingComponent from "../../LoadingComponent";
import TasteTestClaimingSetup from "../TasteTestClaimingSetup";
import TasteTestOrderSuccessView from "../TasteTestOrderSuccessView";

const LazyTasteTestOrderSubmitting = lazy(
  () => import(/* webpackChunkName: "TransactionSubmitting" */ "../TasteTestOrderSubmitting")
);
const LazyOrderError = lazy(
  () => import(/* webpackChunkName: "OrderError" */ "../../OrderComponents/OrderError")
);

interface ITasteTestClaimingFlowProps {
  isSticky?: boolean;
}

const TasteTestClaimingFlow: FC<ITasteTestClaimingFlowProps> = ({ isSticky }) => {
  const [isTxDetailsOpen, setIsTxDetailsOpen] = useState(false);
  const { t } = useI18N();
  const {
    state: {
      derived: { transaction },
      flowData: { flowState },
    },
  } = useOrderContext();
  const {
    handlers: { setActionType },
    state: { tasteTestPersonalization },
  } = useTasteTestContext();
  const { buildTx, submitTx } = useTasteTestTx({ type: "claim" });
  const isBuildingTx = transaction?.state === EOrderTransactionBuilderState.BUILD;
  const isTxBuilt = !!transaction?.completeTx;

  /** Set "claim" as `actionType` once this component mounts and build the transaction. */
  useEffect(() => {
    setActionType("claim");
    if (!isTxBuilt && !transaction?.error) {
      buildTx();
    }
  }, [isTxBuilt, transaction?.error]);

  const handleTxDetailsToggle = useCallback(() => {
    setIsTxDetailsOpen((prev) => !prev);
  }, [setIsTxDetailsOpen]);

  const memoizedClaimingNode = useMemo(() => {
    /* -------------------------------------------------------------------------------------------------
     * Submitting UI
     * -----------------------------------------------------------------------------------------------*/
    if (flowState === EOrderFlowState.submitting) {
      return (
        <Suspense fallback={<LoadingComponent />}>
          <LazyTasteTestOrderSubmitting />
        </Suspense>
      );
    }

    /* -------------------------------------------------------------------------------------------------
     * Order Status (Error) UI
     * -----------------------------------------------------------------------------------------------*/
    if (EOrderFlowState.error === flowState) {
      return (
        <Suspense fallback={<LoadingComponent />}>
          <LazyOrderError />
        </Suspense>
      );
    }

    /* -------------------------------------------------------------------------------------------------
     * Order Status (Success) UI
     * -----------------------------------------------------------------------------------------------*/
    if (EOrderFlowState.success === flowState) {
      return <TasteTestOrderSuccessView />;
    }

    return <TasteTestClaimingSetup isTxDetailsOpen={isTxDetailsOpen} />;
  }, [flowState, isTxDetailsOpen, t, transaction?.state, handleTxDetailsToggle]);

  return (
    <FadeInBasic className="flex flex-col gap-4">
      <div
        className={cx(
          "relative flex min-h-[18rem] w-full flex-col gap-4 overflow-hidden rounded-xl",
          toolkitCx.layout.background.default,
          {
            "animation-delay-100 motion-safe:animate-shadow-pulse": isSticky,
            "justify-center": [
              EOrderFlowState.submitting,
              EOrderFlowState.error,
              EOrderFlowState.success,
            ].includes(flowState),
          }
        )}
      >
        {memoizedClaimingNode}
      </div>

      {![EOrderFlowState.submitting, EOrderFlowState.error, EOrderFlowState.success].includes(
        flowState
      ) && (
        <FadeInBasic className="sticky bottom-4 z-10" transitionDelay={150}>
          <ButtonGroup
            className="w-full"
            widthRatio={["1/2", "1/2"]}
            size="md"
            primaryButtonConfig={{
              buttonProps: {
                className: "flex-1 group w-full hover:-translate-y-[2px]",
                disabled: isBuildingTx,
                onClick: () => submitTx(),
                style: { backgroundColor: tasteTestPersonalization?.primaryColor || "" },
              },
              icon: isBuildingTx ? ArrowPathIcon : ArrowUpRightIcon,
              iconProps: {
                className: cx(
                  {
                    "group-hover:scale-110": !isBuildingTx,
                    "motion-safe:animate-spin": isBuildingTx,
                  },
                  toolkitCx.layout.transition.transform
                ),
              },
              label: isBuildingTx
                ? t("building", { ns: "common", context: "short" })
                : t("detail.widget.claim.cta", { ns: "taste-test" }),
            }}
            secondaryButtonConfig={{
              buttonProps: {
                className: "flex-1 group w-full hover:-translate-y-[2px]",
                onClick: handleTxDetailsToggle,
              },
              icon: isTxDetailsOpen ? XMarkIcon : MagnifyingGlassIcon,
              iconProps: {
                className: cx("group-hover:scale-110", toolkitCx.layout.transition.transform),
              },
              label: `${isTxDetailsOpen ? t("hide") : t("view")} ${t("details", {
                ns: "glossary",
              })}`,
            }}
          />
        </FadeInBasic>
      )}
    </FadeInBasic>
  );
};

export default TasteTestClaimingFlow;
